import {
  CardHeader,
  CardContent,
  CardTitle,
  CardFooter,
} from '@/components/ui/card'
import { AlertTriangle, Server, HardDrive, Cpu, MemoryStick, Zap } from 'lucide-react'
import { type NotificationItemProps, type ShutdownResourceMetadata } from './types'
import { formatTransactionDate } from './utils'
import { MarkAsReadButton, FormattedNotificationContent } from '.'
import { DefaultNotification } from './DefaultNotification'
import { useGetServerInstance } from '@/services/server'
import { useStorageVolumeInfo } from '@/hooks/useStorageVolumeInfo'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

export const ShutdownResourceNotification = ({
  notification,
  isLoading,
  onMarkAsRead,
}: NotificationItemProps) => {
  if (!notification.metadata) {
    return (
      <DefaultNotification
        notification={notification}
        isLoading={isLoading}
        onMarkAsRead={onMarkAsRead}
      />
    )
  }

  const metadata = notification.metadata as ShutdownResourceMetadata
  const hasInstances = metadata.instance_ids?.length > 0
  const hasVolumes = metadata.volume_ids?.length > 0

  return (
    <>
      <CardHeader className="pt-5 pb-3 px-6">
        <div className="flex items-start gap-4">
          <div className="flex items-center justify-center h-10 w-10 rounded-full flex-shrink-0 bg-destructive/10">
            <AlertTriangle className="h-5 w-5 text-destructive" />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-lg font-bold">
              Resources Have Been Shutdown
            </CardTitle>
            <p className="text-sm text-foreground/60 mt-1">
              Due to insufficient balance
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-6 py-4 space-y-4">
        {/* Main notification message */}
        <FormattedNotificationContent
          content={notification.detail || 'Your resources have been terminated.'}
        />

        {/* Terminated Resources Section */}
        {(hasInstances || hasVolumes) && (
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-foreground/80 uppercase tracking-wide">
              Terminated Resources
            </h4>

            {/* Instance Cards */}
            {hasInstances && (
              <div className="space-y-1">
                {metadata.instance_ids.map((instanceId) => (
                  <InstanceCard key={instanceId} instanceId={instanceId} />
                ))}
              </div>
            )}

            {/* Volume Cards */}
            {hasVolumes && (
              <div className="mt-1">
                <VolumeCards volumeIds={metadata.volume_ids} />
              </div>
            )}
          </div>
        )}

        <p className="text-xs text-foreground/60 mt-4">
          Updated on {formatTransactionDate(notification.created_at)}
        </p>
      </CardContent>

      {!notification.is_read && (
        <CardFooter className="px-6 py-3 flex justify-end border-t">
          <MarkAsReadButton
            isLoading={isLoading}
            onMarkAsRead={() => onMarkAsRead(notification.notification_id)}
          />
        </CardFooter>
      )}
    </>
  )
}

// Instance Card Component
const InstanceCard = ({ instanceId }: { instanceId: number }) => {
  const { data: instance, isLoading, isError } = useGetServerInstance(instanceId)

  if (isLoading) {
    return (
      <div className="p-2.5 rounded-md border bg-card">
        <Skeleton className="h-3.5 w-48" />
      </div>
    )
  }

  if (isError || !instance) {
    return (
      <div className="p-2.5 rounded-md border bg-card">
        <div className="flex items-center gap-2">
          <Server className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-medium">Instance #{instanceId}</span>
          <Badge variant="secondary" className="text-xs h-4 px-1.5">
            Unavailable
          </Badge>
        </div>
      </div>
    )
  }

  const cpu = instance.cpu_limit_count || 0
  const ram = instance.ram_limit_gb || 0
  const gpu = instance.gpu_request_count || 0
  const gpuMem = instance.gpu_request_gmem || 0

  return (
    <div className="p-2.5 rounded-md border bg-card hover:bg-accent/50 transition-colors">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Server className="h-3.5 w-3.5 text-primary/70 flex-shrink-0" />
          <span className="text-xs font-medium truncate">
            {instance.instance_name || `Instance #${instanceId}`}
          </span>
          <Badge variant="outline" className="text-xs h-4 px-1.5 flex-shrink-0 border-destructive/50 text-destructive bg-destructive/10">
            Terminated
          </Badge>
        </div>
        <div className="flex items-center gap-2 text-xs text-foreground/60 flex-shrink-0">
          <span>{cpu} vCPU</span>
          <span>•</span>
          <span>{ram} GB</span>
          {gpu > 0 && (
            <>
              <span>•</span>
              <span>{gpu}x GPU</span>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// Volume Cards Component
const VolumeCards = ({ volumeIds }: { volumeIds: number[] }) => {
  // Convert volume IDs to the format expected by useStorageVolumeInfo
  const volumeInfo = volumeIds.map(id => ({
    id: id,
    storage_volume_id: id,
    mount_path: ''
  }))

  const enhancedVolumes = useStorageVolumeInfo(volumeInfo)

  return (
    <div className="grid grid-cols-3 gap-1">
      {enhancedVolumes.map((volume) => (
        <div
          key={volume.storage_volume_id}
          className={cn(
            "p-2.5 rounded-md border bg-card hover:bg-accent/50 transition-colors",
            volume.isLoading && "animate-pulse"
          )}
        >
          {volume.isLoading ? (
            <Skeleton className="h-3.5 w-full" />
          ) : (
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <HardDrive className="h-3.5 w-3.5 text-primary/70 flex-shrink-0" />
                <span className="text-xs font-medium truncate">
                  {volume.volume_name || `Vol #${volume.storage_volume_id}`}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <Badge variant="outline" className="text-xs h-4 px-1.5 border-destructive/50 text-destructive bg-destructive/10">
                  Terminated
                </Badge>
                {volume.size_gb && (
                  <span className="text-xs text-foreground/50">
                    {volume.size_gb}GB
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}